import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import LabelValue from '../../LabelValue';
import { Tier3Provider } from '../../../data/models/provider.model';
import { useBooleanStatus } from '../../../hooks/useBooleanStatus';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';

import * as referenceSlice from '../../../data/reference.slice';
import { FULLSTORY_MASK_CLASSNAME } from '../../../services/analyticsUtils';

interface Tier3ServicesProps {
  provider: Tier3Provider | null;
  titleStyle: string;
  isInternalUser: boolean;
}

export function Tier3Services(props: Tier3ServicesProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const { provider, titleStyle, isInternalUser } = props;
  const paymentMethods = useAppSelector(referenceSlice.selectPaymentTypes);
  const tripModes = useAppSelector(referenceSlice.selectTripModes);

  React.useEffect(() => {
    dispatch(referenceSlice.fetchTripModesAsync());
    dispatch(referenceSlice.fetchPaymentTypesAsync());
  }, []);

  const getCoverageAreas = (provider: Tier3Provider | null) => {
    let coverageAreas = '';
    if (provider?.transportationProviderCoverageAreas) {
      coverageAreas = provider.transportationProviderCoverageAreas
        .map((tpca) => tpca.coverageArea?.coverageAreaName)
        .filter(Boolean)
        .join(', ');
    }
    return coverageAreas;
  };

  const getDailyCapacity = (provider: Tier3Provider | null) => {
    let capacity = '';
    if (provider?.vehicleTripModeCapacities?.length) {
      capacity = String(provider.vehicleTripModeCapacities[0].capacity);
    }
    return capacity !== '' ? capacity : '0';
  };

  const getProviderState = (provider: Tier3Provider | null) => {
    let providerState = '';
    if (provider?.transportationProviderState) {
      providerState = provider.transportationProviderState;
    }
    return providerState === 'active' ? 'Yes' : 'No';
  };

  const getClearToTransport = (provider: Tier3Provider | null) => {
    let ClearToTransport = false;
    if (provider?.isClearToTransport) {
      ClearToTransport = provider.isClearToTransport;
    }
    return ClearToTransport === true ? 'Yes' : 'No';
  };

  const getActivePaymentMethods = React.useMemo(() => {
    return paymentMethods
      .filter(
        (method) =>
          provider?.paymentTypes?.some(
            (pt) => pt.paymentTypeID === method.paymentTypeID,
          ),
      ) // Filter only active payment methods
      .map((method) => method.paymentTypeName) // Map to the paymentTypeName
      .join(', '); // Join the names into a comma-separated string
  }, [paymentMethods, provider?.paymentTypes]); // Dependencies

  const getActiveTripModes = React.useMemo(() => {
    return tripModes
      .filter(
        (method) =>
          provider?.vehicleTripModeCapacities?.some(
            (pt) => pt.tripModeID === method.tripModeID,
          ),
      ) // Filter only active trip modes
      .map((mode) => mode.tripModeName) // Map to the tripModeName
      .join(', '); // Join the names into a comma-separated string
  }, [tripModes, provider?.vehicleTripModeCapacities]); // Dependencies

  return (
    <>
      <Typography className={titleStyle}>{t('providers.services')}</Typography>
      <Grid container spacing={2}>
        <Grid item sm={12} md={6}>
          <LabelValue
            label={t('provider.coverageAreas')}
            value={getCoverageAreas(provider)}
            className={FULLSTORY_MASK_CLASSNAME}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <LabelValue
            label={t('provider.capacity')}
            value={getDailyCapacity(provider)}
            className={FULLSTORY_MASK_CLASSNAME}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <LabelValue
            label={t('provider.isActive')}
            value={getProviderState(provider)}
            className={FULLSTORY_MASK_CLASSNAME}
          />
        </Grid>
        {isInternalUser && (
          <Grid item sm={12} md={6}>
            <LabelValue
              label={t('provider.isInternal')}
              value={useBooleanStatus(provider?.isInternal)}
            />
          </Grid>
        )}
        <Grid item sm={12} md={6}>
          <LabelValue
            label={t('provider.clearToTransport')}
            value={getClearToTransport(provider)}
            className={FULLSTORY_MASK_CLASSNAME}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <LabelValue
            label={t('provider.paymentMethods')}
            value={getActivePaymentMethods} // Use the filtered payment methods
            className={FULLSTORY_MASK_CLASSNAME}
          />
        </Grid>
        <Grid item sm={12} md={6}>
          <LabelValue
            label={t('provider.tripModes')}
            value={getActiveTripModes} // Use the filtered trip modes
            className={FULLSTORY_MASK_CLASSNAME}
          />
        </Grid>
      </Grid>
    </>
  );
}
